import { ILinkProps, P2GBox } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GLinkGroup } from '@parcel2go/core-ui';
import { useEffect } from 'react';
import { useState } from 'react';
import {
  ConsignmentApiModel,
  ConsignmentParcelApiModel,
  ConsignmentParcelItemApiModel,
  Dimensions,
  PackageApiModel
} from '~/lib/api/smartsend/types';
import { tinyId } from '../../../lib/helpers/uid';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { GroupingPanelParcels } from './GroupingPanelParcels';
import { QuantityValueSummary } from '../panels/QuantityValueSummary';
import { SmartSendItemsToConsignment } from '../panels/consignments/SmartSendItemsToConsignment';

export interface props {
  group: SmartSendItemGroup;
  allowExpanding: boolean;
  allowInlineDimensions: boolean;
  onChange?: (items: ConsignmentApiModel, valid: boolean) => void;
  consignment?: ConsignmentApiModel;
  dims: Dimensions;
  dimensionsRequired?: boolean;
  hideDimensionsIfNotRequired?: boolean;
  allowContentChange: boolean;
  totalParcelValue?: number;
  getPackages?: () => Promise<PackageApiModel[]>;
  itemName: string;
}

export const CustomInner: React.FC<props> = (props) => {
  const [parcels, setParcels] = useState<ConsignmentParcelApiModel[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const itemName = props.itemName || 'Parcel';
  const currencyCode = props.group.Items[0]?.CurrencyCode;

  useEffect(() => {
    const ps = props.consignment?.Parcels || SmartSendItemsToConsignment(props.group.Items).Parcels;
    setParcels(ps);
  }, [props.group.Items.length, props.totalParcelValue, props.consignment]);

  useEffect(() => {
    if (props.onChange && parcels && parcels.length > 0) {
      props.onChange({ Parcels: parcels }, parcels.length !== props.group.Items.length);
    }
  }, [parcels]);

  const expand = (existing: ConsignmentParcelItemApiModel) => {
    if (!props.allowExpanding || existing.Quantity == 1) {
      return;
    }
    const ps = [...parcels];

    const parent = ps.find((x) => x.Orders.find((o) => o.UID === existing.UID));
    if (parent) {
      const s = QuantityValueSummary(existing.Value, existing.Quantity);
      for (let index = 1; index < existing.Quantity; index++) {
        parent.Orders.push({
          ...existing,
          UID: tinyId(),
          Quantity: 1,
          Value: s.valueForOtherItems
        });
      }
      parent.Orders.filter((e) => e.UID === existing.UID).forEach((element) => {
        element.Value = s.valueForFirstItem;
        element.Quantity = 1;
      });
    }

    setParcels(ps);
  };

  function getAllOrders(): ConsignmentParcelItemApiModel[] {
    const output: ConsignmentParcelItemApiModel[] = [];
    parcels.forEach((p) => {
      p.Orders.forEach((o) => {
        output.push(o);
      });
    });
    return output;
  }

  function onParcelDimensionsUpdate(uid: string, dimensions: Dimensions) {
    const newList = [...parcels];
    newList.forEach((p) => {
      if (p.UID === uid) {
        p.Dimensions = { ...dimensions };
      }
    });
    setParcels(newList);
  }

  function onContentUpdate(uid: string, desc: string) {
    const newList = [...parcels];
    newList.forEach((p) => {
      p.Orders.forEach((o) => {
        if (o.UID === uid) {
          o.Description = desc;
        }
      });
    });
    setParcels(newList);
  }

  function moveToParcel(uid: string) {
    const newList = [...parcels];
    const allOrders = getAllOrders();
    newList.forEach((p) => {
      p.Orders =
        p.UID === uid
          ? p.Orders.filter((x) => !selected.includes(x.UID)).concat(allOrders.filter((x) => selected.includes(x.UID)))
          : p.Orders.filter((x) => !selected.includes(x.UID));
    });
    setParcels(newList.filter((x) => x.Orders.length > 0));
    setSelected([]);
  }

  function createNewParcel() {
    const newList = [...parcels];
    const allOrders = getAllOrders();

    let listToAddTo = parcels.find(
      (x) => x.CanAdd && x.Orders.filter((x) => selected.includes(x.UID)).length === x.Orders.length
    );

    if (!listToAddTo) {
      listToAddTo = {
        Dimensions: { ...props.dims },
        UID: tinyId(),
        Id: null,
        CanAdd: true,
        Orders: []
      };
      newList.push(listToAddTo);
    }

    newList.forEach((p) => {
      p.Orders =
        p.UID === listToAddTo.UID
          ? allOrders.filter((x) => selected.includes(x.UID))
          : p.Orders.filter((x) => !selected.includes(x.UID));
    });

    setParcels(newList.filter((x) => x.Orders.length > 0));
    setSelected([]);
  }

  function selectionIsFullParcel() {
    return (
      parcels.filter(
        (x) =>
          x.Orders.length === selected.length &&
          x.Orders.length === x.Orders.filter((o) => selected.includes(o.UID)).length
      ).length === 1
    );
  }

  function parcelOptions(): ILinkProps[] {
    const options = parcels
      .filter((p) => p.CanAdd)
      .filter((p) => p.Orders.filter((x) => selected.includes(x.UID)).length === 0)
      .map((p) => (
        <P2GLink key={`move-to-{${itemName}`} onClick={() => moveToParcel(p.UID)}>
          Move to {itemName} {parcels.indexOf(p) + 1}
        </P2GLink>
      ));

    if (selected.length > 0 && !selectionIsFullParcel()) {
      options.push(
        <P2GLink onClick={createNewParcel} key="group-together">
          {selected.length > 1 ? 'Group Together' : `Move to New ${itemName}`}
        </P2GLink>
      );
    }

    return options;
  }

  return (
    <GroupingPanelParcels
      onParcelDimensionsUpdate={onParcelDimensionsUpdate}
      expand={expand}
      parcels={parcels}
      selected={selected}
      updateDescription={props.allowContentChange ? onContentUpdate : null}
      getPackages={props.getPackages}
      dimensionsRequired={props.dimensionsRequired}
      allowInlineDimensions={props.allowInlineDimensions}
      hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
      currencyCode={currencyCode}
      onSelect={(s) => {
        setSelected(selected.includes(s) ? selected.filter((x) => x != s) : selected.concat([s]));
      }}
    >
      {selected.length > 0 && (
        <P2GBox p={2} bordered>
          {parcelOptions().length > 0 ? (
            <P2GLinkGroup links={parcelOptions()} />
          ) : (
            <P2GLink>{selected.length} items selected</P2GLink>
          )}
        </P2GBox>
      )}
    </GroupingPanelParcels>
  );
};
